import { Injectable, OnDestroy } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';
import { Subscription } from 'rxjs';

import { EntityMetaDto, EntityColumnMetaDto, EntityMetaForeignKeyDto, IEntityMetaForeignKeyDto } from 'src/app/api';

import { FieldMetadata, FieldMetadataUtil, NavigationCommandMetadata, NavigationCommandQuery, ViewMetadata } from '@data/cer-data-types';

import { UiCommand, UiCommandEvent } from '@ui/cer-ui-types';

import { CerFormView } from '@form/cer-form-view';
import { CerFormPaneType } from '@form/cer-form-panes/cer-form-panes.service';

import { CerSessionService } from '../cer-session/cer-session.service';
import { CerRouteData } from '@app/app.module-generated';
import { CerAppRouteService } from 'src/cer-app/cer-app-route/cer-app-route.service';

export type CerUiLinkType = 'navMenu' | 'nav' | 'fk';

export class CerFormDataNavigation {
  public url: string;
  public title: string;
  public route: Route;
  public columnNameParent?: string;
  public columnNameChild?: string;
  public filterValue?: any;
  public filterStatic?: boolean = false;
  public filterSingleRow?: boolean = false;
  public mode: 'select' | 'list' | 'card' = 'list';
  public paneTarget: CerFormPaneType;
  public parentView: CerFormView;
  public callerData?: any;
  public link: CerUiLinkType | CerUiLinkType[];
  public command?: UiCommand;
  public mandatory?: boolean;
}

export class CerFormDataNavigationUtil {
  public static initViewMetadata(nav: CerFormDataNavigation, v: ViewMetadata) {
    if (v) {
      v.parentPrimaryKey = nav.columnNameParent;
      v.parentForeignKey = nav.columnNameChild;
    }
  }
}

@Injectable()
export class CerFormNavigationService implements OnDestroy {

  // Subscriptions
  private subscriptionManager: Subscription = new Subscription();

  constructor(
    private router: Router,
    private routeService: CerAppRouteService,
    private session: CerSessionService) {
  }

  ngOnDestroy(): void {
    this.subscriptionManager.unsubscribe();
  }

  public navigationsGet(viewMeta: ViewMetadata, parentView: CerFormView): CerFormDataNavigation[] {
    var navMetas: NavigationCommandMetadata[] = this.navigationsFilterByLink(viewMeta.navigations, ['nav', 'navMenu']);
    viewMeta.fieldMetaData?.filter(f => f.foreignKey?.navigations?.length > 0)
      .forEach(f => {
        this.navigationsFilterByLink(f.foreignKey.navigations, ['nav', 'navMenu'])?.forEach(nav => {
          nav.foreignKey = this.foreignKeyPrepare(nav.foreignKey, viewMeta.dataApiName, f.name);
          navMetas ??= [];
          navMetas.push(nav);
        })
      });
    if (navMetas?.length > 0) {
      var navs: CerFormDataNavigation[] = [];
      navMetas.forEach(navMeta => {
        const fk = this.foreignKeyPrepare(navMeta.foreignKey, viewMeta.dataApiName, null);
        const filterValue: any = null;
        const nav: CerFormDataNavigation = this.navigationByForeignKeyGet(navMeta.url, fk, filterValue, parentView);
        if (nav != null) {
          if (navMeta.link?.length > 0) {
            nav.link = navMeta.link;
          }
          if (navMeta.title?.length > 0) {
            nav.title = navMeta.title;
          }
          nav.paneTarget ??= navMeta.paneTarget;
          navs.push(nav);
        }
      });
      navs = this.navigationsDuplicatesCheck(navs);
      return navs;
    }
    return null;
  }

  private navigationsFilterByLink(navigations: NavigationCommandMetadata[], linksFilter: string[]): NavigationCommandMetadata[] {
    var navs = navigations?.filter(cmd => this.intersetionExists(cmd.link, linksFilter));
    return navs;
  }

  private intersetionExists(a: string[] | string, b: string[]): boolean {
    if (Array.isArray(a)) {
      return a.filter(a2ele => b.includes(a2ele)).length > 0;
    }
    else {
      return b.includes(a as string);
    }
  }

  private navigationsDuplicatesCheck(navs: CerFormDataNavigation[]): any {
    if (navs.length > 1) {
      var maxIdx: number = navs.length - 1;
      for (let idx = maxIdx - 1; idx > 0; idx--) {
        for (let chk = maxIdx; chk > idx; chk--) {
          if (navs[idx].url == navs[chk].url) {
            navs.splice(chk);
            maxIdx = navs.length - 1;
          }
        }
      }
    }
    return navs;
  }

  private entityMetaForeignKeysGet(viewMeta: ViewMetadata, fieldMeta: FieldMetadata): EntityMetaForeignKeyDto {
    var foreignKey: EntityMetaForeignKeyDto = null;
    if (viewMeta != null && fieldMeta != null) {
      const entity: EntityMetaDto = this.session.user.entityByName(viewMeta.entityName ?? viewMeta.dataApiName);
      const column: EntityColumnMetaDto = this.session.user.columnByName(entity, fieldMeta.name);
      if (column != null) {
        const hasFKMeta = fieldMeta.foreignKeyTableName?.length > 0 && FieldMetadataUtil.foreignKeyFieldGet(fieldMeta)?.length > 0;
        foreignKey = hasFKMeta
          ? new EntityMetaForeignKeyDto({ parentEntity: fieldMeta.foreignKeyTableName, parentColumn: FieldMetadataUtil.foreignKeyFieldGet(fieldMeta) })
          : column?.attributes?.foreignKey;
      }
    }
    return foreignKey;
  }

  private fieldForeignKeyUsingGet(viewMeta: ViewMetadata, fieldMeta: FieldMetadata): FieldMetadata {
    if (fieldMeta?.foreignKeyUsing?.length > 0) {
      const fieldMetaUsing = viewMeta.fieldMetaData?.find(f => f.name == fieldMeta.foreignKeyUsing);
      if (fieldMetaUsing) {
        return fieldMetaUsing;
      }
    }
    return fieldMeta;
  }

  /*
  return this.navigationByForeignKey(foreignKey, formView, column.name, keyValue);
  public navigationByForeignKey(foreignKey: EntityMetaForeignKeyDto, formparentView: CerFormView, keyName: string, keyValue: any = null): CerFormDataNavigation[] {
    return this.navigationByEntityColumn(foreignKey.entity, foreignKey.column, formparentView, foreignKey.url, keyName, keyValue);
  }
  */

  public navigationByEntityColumnGet(entityNameChild: string, columnNameChild: string = null, columnNameParent: string, filterValue: any = null, formparentView: CerFormView = null, isFilterActive: boolean = true): CerFormDataNavigation {

    var route: Route = this.routeByEntityNameGet(entityNameChild);
    if (route == null) {
      return null;
    }
    const nav = this.navigationByRoute(route, entityNameChild, columnNameChild, columnNameParent, filterValue, formparentView, isFilterActive);
    nav.link = 'fk';
    return nav;
  }

  public navigationByForeignKeyGet(url: string, fk: EntityMetaForeignKeyDto, filterValue: any = null, formparentView: CerFormView = null, isFilterActive: boolean = true): CerFormDataNavigation {
    var route: Route = this.routeGetByUrl(url) ?? this.routeByEntityNameGet(fk?.entity);
    return this.navigationByRoute(route, fk.entity, fk.column, fk.parentColumn, filterValue, formparentView, isFilterActive);
  }

  private navigationByRoute(route: Route, entityNameChild: string, columnNameChild: string, columnNameParent: string, filterValue: any, parentView: CerFormView, isFilterActive: boolean): CerFormDataNavigation {
    if (route == null) {
      return null;
    }
    const routeData: CerRouteData = route.data;
    if (!this.session.hasUserRoles(routeData?.roles)) {
      return null;
    }
    const filterSingleRow: boolean = isFilterActive && this.isFieldPrimaryKey(routeData, entityNameChild, columnNameChild);

    const nav = new CerFormDataNavigation();
    nav.url = route.path;
    nav.title = this.navigationTitleGet(route, filterSingleRow);
    nav.route = route;
    nav.columnNameChild = this.nameCaseAdjust(columnNameChild);
    nav.columnNameParent = this.nameCaseAdjust(columnNameParent);
    nav.filterValue = filterValue;
    nav.filterSingleRow = filterSingleRow;
    nav.filterStatic = false; //filterValue is a constant (columnNameParent is not needed)
    nav.parentView = parentView;
    return nav;
  }

  private isFieldPrimaryKey(routeData: CerRouteData, entityName: string, columnName: string): boolean {
    var isPK = false;
    var hasKeys = false;
    const col: string = columnName?.toLocaleLowerCase();

    if (entityName?.length > 0) {
      if (routeData.entities != null) {
        const entity = routeData.entities.find(f => f.name?.toLowerCase == entityName.toLowerCase);
        if (entity.keys != null) {
          hasKeys = true;
          const keys = Array.isArray(entity.keys) ? (<string[]>entity.keys).map(k => k.toLowerCase()) : [<string>entity.keys];
          isPK = keys?.includes(col);
        }
      }
    }
    if (!hasKeys) {
      isPK = (col == 'id' || col == 'num');
    }

    return isPK;
  }

  private navigationTitleGet(route: Route, isPKFilter: boolean): string {
    const data: CerRouteData = route.data;
    if (isPKFilter) {
      const title = data?.titleSingular;
      if (title?.length > 0) {
        return title;
      }
    }
    if (data?.title.length > 0) {
      return data.title;
    }
    return <string>route.title;
  }

  private routeByEntityNameGet(entityName: string): Route {
    var entity: EntityMetaDto = this.session.user.entityByName(entityName);
    if (entity?.name?.length > 0) {
      entityName = entity.name;
    }

    return this.routeGetByUrl(entity?.url)
      ?? CerFormNavigationService.routeGetByEntityName(entityName, this.router.config);
  }

  private nameCaseAdjust(name: string): string {
    return (name?.length > 0)
      ? name[0].toLowerCase() + name.slice(1)
      : null;
  }

  public navigationFromUiCommandEvent(event: UiCommandEvent): CerFormDataNavigation {
    var nav = <CerFormDataNavigation>event.command?.data;
    if (event.rowData && nav.columnNameParent?.length > 0) {
      nav.filterValue = event.rowData[nav.columnNameParent];
    }
    return nav;
  }

  public navigationFromUiCommandColumnFK(event: UiCommandEvent, isFiltered: boolean): CerFormDataNavigation[] {
    return this.navigationFromUiCommandColumn(event, true, isFiltered);
  }

  public navigationFromUiCommandColumn(event: UiCommandEvent, isFKLink: boolean, isFilterActive: boolean): CerFormDataNavigation[] {
    if (event.viewMetadata == null) {
      return [];
    }

    const viewMeta: ViewMetadata = event.viewMetadata;
    const rowData: any = event.rowData;
    const rowDataSelected: any = rowData ?? event.appComponent?.rowSelectedData();
    const fieldMetaOrig: FieldMetadata = event.args?.column?.meta;
    const fieldMetaUsing = fieldMetaOrig != null ? this.fieldForeignKeyUsingGet(viewMeta, fieldMetaOrig) : null;
    const formView = CerFormView.formViewByUiEvent(event);
    const navsMeta = this.navigationsFilterByLink(this.navigationsMetaGet(viewMeta, fieldMetaUsing, rowData, rowDataSelected), isFKLink ? ['fk'] : ['nav', 'navMenu']);
    return this.navigationsFromMeta(navsMeta, viewMeta, fieldMetaUsing, formView, rowData, isFilterActive);
  }

  private navigationsMetaGet(viewMeta: ViewMetadata, fieldMeta: FieldMetadata, rowData: any, rowDataSelected: any): NavigationCommandMetadata[] {
    const query: NavigationCommandQuery = { viewMeta: viewMeta, fieldMeta: fieldMeta, rowData: rowData, rowDataSelected: rowDataSelected };
    var cmdsMeta: NavigationCommandMetadata[] = [];

    // View meta query
    if (viewMeta.navigations?.length > 0) {
      cmdsMeta = cmdsMeta.concat(viewMeta.navigations);
    }
    if (viewMeta.onBeforeNavigations != null) {
      const navCmds = viewMeta.onBeforeNavigations(query);
      if (navCmds?.length > 0) {
        cmdsMeta = cmdsMeta.concat(navCmds);
      }
    }

    // Field meta query
    if (fieldMeta != null) {
      const fk = this.entityMetaForeignKeysGet(viewMeta, fieldMeta);
      if (fk) {
        cmdsMeta.push({ link: 'fk', foreignKey: fk });
      }

      const fkMeta = fieldMeta.foreignKey;
      if (fkMeta?.onBeforeNavigations) {
        const navCmds = fkMeta.onBeforeNavigations(query);
        if (navCmds?.length > 0) {
          cmdsMeta = cmdsMeta.concat(navCmds);
        }
      }

      if (fkMeta?.navigations?.length > 0) {
        cmdsMeta = cmdsMeta.concat(fkMeta.navigations);
      }
    }
    return cmdsMeta;
  }

  private navigationsFromMeta(cmdsMeta: NavigationCommandMetadata[], viewMeta: ViewMetadata, fieldMeta: FieldMetadata, formView: CerFormView, rowData: any, isFilterActive: boolean): CerFormDataNavigation[] {
    const navs: CerFormDataNavigation[] = [];
    cmdsMeta?.forEach(cmdMeta => {
      const nav = this.navigationFromCommandMeta(cmdMeta, viewMeta, fieldMeta, formView, rowData, isFilterActive);
      if (nav) {
        const isValid = isFilterActive ? this.navigationFieldValidate(nav, fieldMeta) : true;
        if (isValid) {
          navs.push(nav);
        }
      }
    });
    return navs;
  }

  private navigationFieldValidate(nav: CerFormDataNavigation, fieldMeta: FieldMetadata): boolean {
    var isValid = false;
    const fieldName = fieldMeta?.name;
    if (fieldName?.length > 0) {
      isValid = (nav.columnNameParent == fieldName);
    }
    return isValid;
  }

  private navigationFromCommandMeta(cmdMeta: NavigationCommandMetadata, viewMeta: ViewMetadata, fieldMeta: FieldMetadata, formView: CerFormView, rowData: any, isFilterActive: boolean = true): CerFormDataNavigation {

    if (cmdMeta.foreignKey == null) {
      return null;
    }

    const fk = this.foreignKeyPrepare(cmdMeta.foreignKey, viewMeta.dataApiName, fieldMeta?.name);
    const keyValue: any = fk?.parentColumn?.length && rowData != null ? rowData[fk.parentColumn] : null;
    const nav = this.navigationByForeignKeyGet(cmdMeta.url, fk, keyValue, formView, isFilterActive);
    if (nav) {
      if (cmdMeta.title?.length > 0) {
        nav.title = cmdMeta.title;
      }
      if (cmdMeta.paneTarget != null) {
        nav.paneTarget = cmdMeta.paneTarget;
      }
    }
    return nav;
  }

  private foreignKeyPrepare(fkDto: IEntityMetaForeignKeyDto, parentEntity: string, parentColumn: string): EntityMetaForeignKeyDto {
    const fk = new EntityMetaForeignKeyDto(fkDto);

    if (parentEntity?.length > 0) {
      if (fk.entity == null || fk.entity?.length == 0 || fk.entity == parentEntity) {
        // Parent entity should be the current view, with navigation to new child view
        // Switch/reverse relation
        fk.entity = fk.parentEntity;
        const columnSwitch = fk.column;
        fk.column = fk.parentColumn;
        fk.parentEntity = parentEntity;
        fk.parentColumn = columnSwitch;
      }
    }
    fk.parentEntity ??= parentEntity;
    fk.parentColumn ??= parentColumn;
    return fk;
  }

  public static routeGetByEntityName(entityName: string, routerConfig: Routes): Route {
    var route: Route = null;
    if (entityName?.length > 0) {
      routerConfig.forEach(r => {
        if (route == null) {
          const routeEntities = (r.data as CerRouteData)?.entities;
          routeEntities?.forEach(e => {
            if (e?.name == entityName) {
              route = r;
            }
          });
          if (route == null) {
            if (r.children?.length > 0) {
              route = CerFormNavigationService.routeGetByEntityName(entityName, r.children);
            }
          }
        }
      });
    }
    return route;
  }

  public routeGetByUrl(url: string): Route {
    return this.routeService.routeGetByUrl(url);
  }
}
